/**
 * THE CONTEXT PANE: the record, then the three controls that change it.
 *
 * The record itself — every field, and the four separate claims about what is
 * established — lives in DocumentRecordDetails, which imports no radix
 * primitive and is therefore assertable against real markup in a unit test.
 * This module adds only the mutation form: status, review status, version and
 * delete, each of which the documents API already owns
 * (`PUT /projects/{id}/documents/{id}`, `DELETE …`).
 *
 * The controls live HERE rather than on every list row. The page this
 * replaces put two selects, a number input and two icon buttons on each row,
 * which is what made the list 764px wide on a 412px phone and what made a
 * keyboard user pass five controls to reach the second document. One document
 * is being looked at; its controls belong beside it.
 *
 * Nothing in this file adds a control whose contract does not exist. There is
 * no markup tool, no revision comparison, no linkage editor and no disabled
 * placeholder standing in for one.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Trash2 } from 'lucide-react';
import {
  DOCUMENT_STATUSES,
  DOCUMENT_REVIEW_STATUSES,
} from './documentPresentation';
import { DocumentRecordDetails } from './DocumentRecordDetails';

export {
  AvailabilityBreakdown,
  DocumentRecordDetails,
} from './DocumentRecordDetails';

export const DocumentContextPanel = ({
  document: doc,
  onUpdate,
  onDelete,
  onDownload,
  mutating = false,
  canMutate = true,
}) => {
  if (!doc) return <DocumentRecordDetails document={null} />;

  return (
    <div
      data-testid="document-context"
      data-document-id={doc.id}
      className="flex flex-col gap-4 p-4"
    >
      <DocumentRecordDetails document={doc} onDownload={onDownload} />

      {canMutate && (
        <div className="flex flex-col gap-3 border-t border-border/60 pt-3">
          <p className="text-[0.7rem] uppercase tracking-wide text-muted-foreground">
            Record
          </p>

          <div>
            <Label htmlFor={`status-${doc.id}`} className="text-xs">
              Document status
            </Label>
            <Select
              value={doc.status || 'draft'}
              onValueChange={(value) => onUpdate?.(doc, { status: value })}
              disabled={mutating}
            >
              <SelectTrigger
                id={`status-${doc.id}`}
                data-testid="context-status-select"
                className="mt-1 h-9 bg-muted/50"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor={`review-${doc.id}`} className="text-xs">
              Review status
            </Label>
            <Select
              value={doc.reviewStatus || 'not_reviewed'}
              onValueChange={(value) =>
                onUpdate?.(doc, { review_status: value })
              }
              disabled={mutating}
            >
              <SelectTrigger
                id={`review-${doc.id}`}
                data-testid="context-review-select"
                className="mt-1 h-9 bg-muted/50"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_REVIEW_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor={`version-${doc.id}`} className="text-xs">
              Version
            </Label>
            <Input
              id={`version-${doc.id}`}
              data-testid="context-version-input"
              type="number"
              min="1"
              step="1"
              /*
                Keyed on the document so switching selection re-seeds the field
                instead of showing the previous drawing's version number under
                this one's name.
              */
              key={`version-${doc.id}-${doc.version}`}
              defaultValue={doc.version || 1}
              disabled={mutating}
              onBlur={(event) => {
                const next = Math.max(1, Number(event.target.value) || 1);
                if (next !== doc.version) onUpdate?.(doc, { version: next });
              }}
              className="mt-1 h-9 bg-muted/50 font-mono text-xs"
            />
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete?.(doc)}
            disabled={mutating}
            data-testid="context-delete-btn"
            className="justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete document
          </Button>
        </div>
      )}
    </div>
  );
};

export default DocumentContextPanel;
